"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { imageUrl } from "@/lib/image-url";
import { useGameLocale, useT } from "@/lib/i18n";
import { categoryDescription, categoryLabel, channelLabel, folderLabel, parseChannel } from "@/lib/image-categories";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

// Folders above this ship no zip button; the backend enforces the same cap.
const ZIP_MAX_FILES = 2000;
interface ImageEntry {
  filename: string;
  url: string;
}
interface Category {
  id: string;
  name: string;
  count: number;
  images: ImageEntry[];
  // `images` is only a preview; the contents page through /browse.
  browse: {
    version: string;
    category: string;
  };
}
interface BrowsePage {
  path: string;
  folders: string[];
  total: number;
  offset: number;
  limit: number;
  images: ImageEntry[];
}
function ImageTile(t0) {
  const $ = _c(14);
  const {
    img
  } = t0;
  let t1;
  if ($[0] !== img.filename) {
    let t2;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = /_/g;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = img.filename.replace(/\.(png|webp|gif|jpe?g)$/i, "").replace(t2, " ");
    $[0] = img.filename;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  let t2;
  if ($[3] !== img.url) {
    t2 = imageUrl(img.url);
    $[3] = img.url;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== label || $[6] !== t2) {
    t3 = <div className="flex items-center justify-center p-2"><img src={t2} alt={label} crossOrigin="anonymous" loading="lazy" className="max-w-full object-contain group-hover:scale-105 transition-transform duration-300" /></div>;
    $[5] = label;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== img.filename || $[9] !== label) {
    t4 = <div className="px-1.5 pb-1.5 text-center"><span className="text-[10px] text-[var(--text-muted)] truncate block" title={img.filename}>{label}</span></div>;
    $[8] = img.filename;
    $[9] = label;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="group rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] hover:border-[var(--border-accent)] transition-all overflow-hidden">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}

// Folder-aware pager for the dump categories: the contents live on the CDN
// and page through /api/images/game/<version>/<category>/browse, so even the
// six-figure card cross-product trees stay navigable. Each folder within the
// zip cap gets a download button for its own files.
function GameBrowser(t0) {
  const $ = _c(63);
  const {
    version,
    category
  } = t0;
  const t = useT();
  const lang = useGameLocale();
  const [path, setPath] = useState("");
  const [page, setPage] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [images, setImages] = useState(t1);
  const [loading, setLoading] = useState(false);
  let t2;
  let t3;
  if ($[1] !== category || $[2] !== path || $[3] !== version) {
    t2 = () => {
      setLoading(true);
      setImages([]);
      fetch(`${API}/api/images/game/${version}/${category}/browse?path=${encodeURIComponent(path)}&offset=0&limit=200`).then(_temp).then(data => {
        setPage(data);
        setImages(data.images ?? []);
      }).finally(() => setLoading(false));
    };
    t3 = [version, category, path];
    $[1] = category;
    $[2] = path;
    $[3] = version;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== category || $[7] !== images.length || $[8] !== page || $[9] !== path || $[10] !== version) {
    t4 = function loadMore() {
      if (!page) {
        return;
      }
      setLoading(true);
      fetch(`${API}/api/images/game/${version}/${category}/browse?path=${encodeURIComponent(path)}&offset=${images.length}&limit=200`).then(_temp2).then(data_0 => setImages(prev => [...prev, ...(data_0.images ?? [])])).finally(() => setLoading(false));
    };
    $[6] = category;
    $[7] = images.length;
    $[8] = page;
    $[9] = path;
    $[10] = version;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const loadMore = t4;
  let canZip;
  let t5;
  let t6;
  let t7;
  if ($[12] !== category || $[13] !== lang || $[14] !== page || $[15] !== path) {
    const crumbs = path ? path.split("/") : [];
    canZip = page != null && page.total > 0 && page.total <= ZIP_MAX_FILES;
    t5 = "flex flex-wrap items-center gap-1.5 mb-3 text-xs";
    let t8;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = () => setPath("");
      $[20] = t8;
    } else {
      t8 = $[20];
    }
    const t9 = `px-2 py-0.5 rounded ${path === "" ? "text-[var(--accent-gold)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`;
    let t10;
    if ($[21] !== category || $[22] !== lang) {
      t10 = categoryLabel(category, lang);
      $[21] = category;
      $[22] = lang;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    if ($[24] !== category || $[25] !== t10 || $[26] !== t9) {
      t6 = <button type="button" onClick={t8} title={category} className={t9}>{t10}</button>;
      $[24] = category;
      $[25] = t10;
      $[26] = t9;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    t7 = crumbs.map((seg, i) => <span key={i} className="flex items-center gap-1.5"><span className="text-[var(--text-muted)]">/</span><button type="button" onClick={() => setPath(crumbs.slice(0, i + 1).join("/"))} title={seg} className={`px-1 py-0.5 rounded ${i === crumbs.length - 1 ? "text-[var(--accent-gold)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}>{folderLabel(seg, lang)}</button></span>);
    $[12] = category;
    $[13] = lang;
    $[14] = page;
    $[15] = path;
    $[16] = canZip;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    canZip = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
  }
  let t8;
  if ($[28] !== canZip || $[29] !== category || $[30] !== page || $[31] !== path || $[32] !== t || $[33] !== version) {
    t8 = page && <span className="ml-auto flex items-center gap-2"><span className="text-[var(--text-muted)]">{t("{n} files", {
          n: page.total
        })}{page.folders.length > 0 ? `, ${t("{n} folders", {
          n: page.folders.length
        })}` : ""}</span>{canZip && <a href={`${API}/api/images/game/${version}/${category}/download?path=${encodeURIComponent(path)}`} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-[var(--accent-gold)] text-[var(--bg-primary)] hover:opacity-90 transition-opacity"><svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V3" /></svg>{t("Download ZIP")}</a>}</span>;
    $[28] = canZip;
    $[29] = category;
    $[30] = page;
    $[31] = path;
    $[32] = t;
    $[33] = version;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8) {
    t9 = <div className={t5}>{t6}{t7}{t8}</div>;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  let t10;
  if ($[40] !== lang || $[41] !== page || $[42] !== path) {
    t10 = page && page.folders.length > 0 && <div className="flex flex-wrap gap-2 mb-3">{page.folders.map(f => <button key={f} type="button" onClick={() => setPath(path ? `${path}/${f}` : f)} title={f} className="px-2.5 py-1 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-primary)] text-xs text-[var(--text-secondary)] hover:border-[var(--border-accent)] hover:text-[var(--text-primary)] transition-colors">{folderLabel(f, lang)}/</button>)}</div>;
    $[40] = lang;
    $[41] = page;
    $[42] = path;
    $[43] = t10;
  } else {
    t10 = $[43];
  }
  let t11;
  if ($[44] !== images) {
    t11 = images.map(_temp3);
    $[44] = images;
    $[45] = t11;
  } else {
    t11 = $[45];
  }
  let t12;
  if ($[46] !== t11) {
    t12 = <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">{t11}</div>;
    $[46] = t11;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== loading || $[49] !== t) {
    t13 = loading && <div className="text-center py-4 text-xs text-[var(--text-muted)]">{t("Loading...")}</div>;
    $[48] = loading;
    $[49] = t;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== images.length || $[52] !== loadMore || $[53] !== loading || $[54] !== page || $[55] !== t) {
    t14 = !loading && page && images.length < page.total && <div className="text-center mt-4"><button type="button" onClick={loadMore} className="px-4 py-1.5 rounded-full text-xs font-medium border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--accent-gold)]/50 hover:text-[var(--text-primary)] transition-colors">{t("Load more ({shown} of {total})", {
          shown: images.length,
          total: page.total
        })}</button></div>;
    $[51] = images.length;
    $[52] = loadMore;
    $[53] = loading;
    $[54] = page;
    $[55] = t;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== t10 || $[58] !== t12 || $[59] !== t13 || $[60] !== t14 || $[61] !== t9) {
    t15 = <div>{t9}{t10}{t12}{t13}{t14}</div>;
    $[57] = t10;
    $[58] = t12;
    $[59] = t13;
    $[60] = t14;
    $[61] = t9;
    $[62] = t15;
  } else {
    t15 = $[62];
  }
  return t15;
}
function _temp3(img) {
  return <ImageTile key={img.url} img={img} />;
}
function _temp2(r_0) {
  return r_0.json();
}
function _temp(r) {
  return r.json();
}
export default function ImagesPage() {
  const $ = _c(23);
  const t = useT();
  const lang = useGameLocale();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [categories, setCategories] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [expanded, setExpanded] = useState(t1);
  const [loading, setLoading] = useState(true);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      fetch(`${API}/api/images`).then(_temp4).then(data => setCategories(data)).finally(() => setLoading(false));
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function toggleCategory(id) {
      setExpanded(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const toggleCategory = t4;
  let t5;
  if ($[5] !== t) {
    t5 = t("Images");
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <h1 className="text-3xl font-bold mb-2"><span className="text-[var(--accent-gold)]">{t5}</span></h1>;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t) {
    t7 = t("The game's complete asset dumps, straight from the engine: the main patch and the current Steam beta side by side. Click a category to browse its folders, or grab any folder as a zip.");
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <p className="text-sm text-[var(--text-muted)] mb-8">{t7}</p>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== categories || $[14] !== expanded || $[15] !== lang || $[16] !== loading || $[17] !== t) {
    t9 = loading ? <div className="text-center py-12 text-[var(--text-muted)]">{t("Loading...")}</div> : <div className="space-y-3">{categories.map(cat => {
        const isOpen = expanded.has(cat.id);
        const label = categoryLabel(cat.browse.category, lang);
        const channel = channelLabel(parseChannel(cat.name), cat.browse.version, lang);
        const description = categoryDescription(cat.browse.category, lang);
        return <div key={cat.id} className="bg-[var(--bg-card)] rounded-lg border border-[var(--border-subtle)] overflow-hidden"><div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-[var(--bg-card-hover)] transition-colors" onClick={() => toggleCategory(cat.id)}><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><span className={`inline-block transition-transform text-[var(--text-muted)] text-xs ${isOpen ? "rotate-90" : ""}`}>{">"}</span><span className="font-semibold text-[var(--text-primary)]">{label}</span><span className="text-xs text-[var(--text-muted)]">{t("{n} images", {
                    n: cat.count
                  })}</span></div>{description && <p className="mt-0.5 pl-5 text-xs text-[var(--text-muted)]">{description}</p>}</div><span className="shrink-0 pl-3 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">{channel}</span></div>{isOpen && <div className="border-t border-[var(--border-subtle)] px-4 pb-4 pt-3"><GameBrowser version={cat.browse.version} category={cat.browse.category} /></div>}</div>;
      })}</div>;
    $[13] = categories;
    $[14] = expanded;
    $[15] = lang;
    $[16] = loading;
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t6 || $[20] !== t8 || $[21] !== t9) {
    t10 = <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{t6}{t8}{t9}</div>;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}
function _temp4(r) {
  return r.json();
}